import {
  get,
  intersection,
  isArray,
  isEqual,
  isEmpty,
  has,
  omit,
  pick,
  pickBy,
  reduce,
  zipObject,
  concat,
  cloneDeep,
} from 'lodash';
import { Utils } from '@riseart/utils';
import { DIMENSIONS } from './config/dimensions.js';
import { FILTER_URL_TRANSLATIONS } from './config/translations.js';
import {
  combineUrlParams,
  validatedUrlParams,
  parseQueryString,
  updateValues,
  removeValues,
  mapParameterToConfig,
  assembleUrl,
  flattenByKey,
  checkDimensionValueConfigMeta,
} from './utils.js';
import { FilterConvertFunctions } from './converters.js';

export const FILTERS_KEY = 'filters';

const OPTION_LABEL_SEPARATOR = ' > ';
const OPTION_VALUE_SEPARATOR = '::';

/**
 * structUrlParams
 * Structure parameters by passing a combined parameter object
 * and a newKey and keys to be omitted in the new key object
 *
 * @param {Record<string, any>} params
 * @param {Record<string, any>} config
 * @returns {Record<string, any>}
 */
function structUrlParams(params: Record<string, any> = {}, config: Record<string, any> = {}): Record<string, any> {
  const filterKeys = reduce(
    config,
    (res: Array<string>, item: Record<string, any>, key: string) => {
      res.push(key);
      if (!isEmpty(item.childDimension)) {
        const [childKey] = Object.keys(item.childDimension);
        res.push(childKey);
      }
      return res;
    },
    [],
  );

  return {
    filters: pick(params, filterKeys),
    ...omit(params, filterKeys),
  };
}

/**
 * FilterService Class
 */
export class FilterService {
  static locale: string;

  static unitLength = 'centimeter';

  filters: Record<string, any> = null;

  qsParamsTranslations: Record<string, any> = {};

  filterUrlTranslations: Record<string, any> = {};

  filterConfig: Record<string, any> = {};

  translator: any;

  defaultParamValues: Record<string, any> = {};

  /**
   * constructor
   *
   * @param {Object} filterConfig
   */
  constructor(filterConfig?: Record<string, any>, translator?: any, defaultParamValues: Record<string, any> = {}) {
    if (filterConfig) {
      this.filterConfig = { ...filterConfig };
      this.qsParamsTranslations =
        FILTER_URL_TRANSLATIONS[filterConfig.locale || FilterService.locale].queryStringParams;
      this.filterUrlTranslations =
        FILTER_URL_TRANSLATIONS[filterConfig.locale || FilterService.locale].filtersInUrl[
          this.filterConfig.backendDomain
        ];

      // Check if all inPath dimensions are existing in root dimensions as well
      if (this.filterUrlTranslations && this.filterUrlTranslations.inPath) {
        // iterate over all inPath dimension keys and check if they exist as root key in this.filterUrlTranslations
        this.filterUrlTranslations = Object.keys(this.filterUrlTranslations.inPath).reduce(
          (accumulator, inPathDimensionKey) => {
            if (!accumulator[inPathDimensionKey]) {
              return { ...accumulator, [inPathDimensionKey]: {} };
            }

            return accumulator;
          },
          { ...(this.filterUrlTranslations || {}) },
        );
      }
    }

    this.translator = translator || ((key) => key);
    this.defaultParamValues = defaultParamValues;
  }

  /**
   * setLocale
   *
   * @param {sting} localeName
   */
  static setLocale(localeName: string): void {
    FilterService.locale = localeName;
  }

  /**
   * setUnitLength
   *
   * @param {sting} localeName
   */
  static setUnitLength(unitLength: string): void {
    if (unitLength) {
      FilterService.unitLength = unitLength.toLowerCase();
    }
  }

  /**
   * hydrate
   *
   * @param {Record<string, any>} filters
   * @returns
   */
  hydrate(filters: Record<string, any>): FilterService {
    this.filters = filters;

    return this;
  }

  /**
   * getConfig
   *
   * @returns {Record<string, any>}
   */
  getConfig(): Record<string, any> {
    return {
      ...this.filterConfig,
      dimensions: this.getDimensionsWithValues(this.filterConfig.backendDomain),
    };
  }

  /**
   * getTranslator
   *
   * @returns {Function}
   */
  getTranslator(): (params: Record<string, any> | string) => any {
    return this.translator;
  }

  /**
   * setTranslator
   *
   * @param translator
   */
  setTranslator(translator: (params: Record<string, any> | string) => any): void {
    if (typeof translator === 'function') {
      this.translator = translator;
    }
  }

  /**
   * getTranslatedKey
   *
   * @param {string} key
   * @param {string} dimension?
   * @param {any} value?
   * @returns {string}
   */
  getTranslatedKey(key: string, dimension?: string, value?: any): string {
    const config = this.getConfig();
    const dimensionConfig = dimension && config.dimensions[dimension];

    if (value !== undefined && dimensionConfig && dimensionConfig.translate === false) {
      return value;
    }

    const translationId =
      dimensionConfig && dimensionConfig.translationPrefix && value !== undefined
        ? `${dimensionConfig.translationPrefix}${value}`
        : `filters.${config.translationDomain}.${key}`;
    return this.getTranslator()({ id: translationId });
  }

  /**
   * getDimensionsWithValues
   * Maps dimension value keys to actual values objects
   *
   * @param {string} domain
   * @returns {Record<string, any>}
   */
  getDimensionsWithValues(domain: string): Record<string, any> {
    const dimensions = DIMENSIONS.domains[domain || this.getConfig().backendDomain] || {};

    return Object.keys(dimensions).reduce((accumulator, dimensionKey) => {
      const dimensionConfig = dimensions[dimensionKey];
      const generatedValues = FilterService.generateValues(dimensionConfig);

      accumulator[dimensionKey] = {
        ...dimensions[dimensionKey],
        ...(generatedValues && generatedValues.modifier ? { modifier: generatedValues.modifier } : null),
        values: generatedValues.values || generatedValues,
      };

      return accumulator;
    }, {});
  }

  /**
   * generateValues
   *
   * @param {Record<string, any>} dimensionConfig
   * @returns {any}
   */
  static generateValues(dimensionConfig: Record<string, any>): any {
    const values: string | string[] | Record<string, any> = dimensionConfig.values;

    if (typeof values === 'string') {
      return get(DIMENSIONS.values, values);
    }

    if (isArray(values)) {
      return values;
    }

    if (values && typeof values === 'object' && values.type === '__custom') {
      switch (values.transform) {
        case 'range': {
          if (values.conditional) {
            return values.config.reduce((accumulator, configItem) => {
              if (FilterService[configItem.condition.property] === configItem.condition.value) {
                return {
                  condition: configItem.condition,
                  modifier: configItem.modifier,
                  values: configItem.steps.reduce((accumulatedValues, stepConfigItem) => {
                    const tempAccumulatedValues = { ...accumulatedValues };
                    for (let i = stepConfigItem.from; i <= stepConfigItem.to; i += stepConfigItem.step) {
                      tempAccumulatedValues[i] = {};
                    }

                    return tempAccumulatedValues;
                  }, {}),
                };
              }

              return accumulator;
            }, {});
          }

          return values.config.reduce((accumulator, configItem) => {
            for (let i = configItem.from; i <= configItem.to; i += configItem.step) {
              accumulator[i] = {};
            }

            return accumulator;
          }, {});
        }
        default:
          return values;
      }
    }

    return values;
  }

  /**
   * route
   *
   * @param {Array<?string>} path
   * @param {Object | string} query
   * @return {Object}
   */
  route(path: Array<string> = [], query: Record<string, any> | string = {}): Record<string, any> {
    const dimensionsConfig = this.getConfig().dimensions;
    const combinedData = combineUrlParams(
      path,
      this.translateQueryString(query),
      dimensionsConfig,
      this.translateFilterValue.bind(this),
    );
    const validatedData = validatedUrlParams(combinedData, dimensionsConfig);

    return structUrlParams(validatedData, dimensionsConfig);
  }

  /**
   * convertDimension
   *
   * @param {number} value
   * @param {string} dimension
   * @param {Record<string, any>} options
   * @returns {any}
   */
  convertDimension(value: any, dimension: string, options?: Record<string, any>): any {
    const config = this.getConfig();
    const dimensionConfig = config.dimensions[dimension];

    if (dimensionConfig && dimensionConfig.modifier) {
      const convertHandler = FilterConvertFunctions[dimensionConfig.modifier.name];

      if (typeof convertHandler === 'function') {
        return convertHandler(value, dimensionConfig.modifier.args, options);
      }
    }

    return value;
  }

  /**
   * assemble
   *
   * @param {Record<string, any>} filter
   * @param {Record<string, any>} parameters
   * @param {Record<string, any>} excludedValues
   * @returns {string}
   */
  assemble(
    filter?: Record<string, any>,
    parameters: Record<string, any> = {},
    excludedValues: Record<string, any> = this.defaultParamValues,
  ): string {
    const combinedValues = this.combineNonDefaultValues(filter || this.filters, parameters, excludedValues);

    return assembleUrl(
      combinedValues,
      this.getConfig(),
      this.translateQueryStringParameter.bind(this),
      this.translateFilterValue.bind(this),
    );
  }

  /**
   * assembleCanonicalUrl
   *
   * @param {Record<string, any>} filters
   * @returns {string}
   */
  assembleCanonicalUrl(filters: Record<string, any>): string {
    // filters out any filter dimensions configured as non canonical
    const includeCanonicalFiltersOnly = (item, { canonical }) => checkDimensionValueConfigMeta(item, canonical);

    return assembleUrl(
      filters,
      this.getConfig(),
      this.translateQueryStringParameter.bind(this),
      this.translateFilterValue.bind(this),
      includeCanonicalFiltersOnly,
    );
  }

  /**
   * translateQueryStringParameter
   *
   * Translates query string parameter (not values).
   * If byKey is set it will translate the value from provided key,
   * otherwise it will search for th traanslation value and return the key
   *
   * @param {string} value
   * @param {boolean} byKey
   * @returns {string}
   */
  translateQueryStringParameter(value: string, byKey = true): string {
    if (byKey) {
      return this.qsParamsTranslations[value] || value;
    }

    let translationKey = value;
    Object.keys(this.qsParamsTranslations).some((key) => {
      const foundKey = this.qsParamsTranslations[key] === value;

      if (foundKey) {
        translationKey = key;
      }

      return foundKey;
    });

    return translationKey;
  }

  /**
   * translateFilterParameter
   *
   * Translates filter values by provided value (or key if byKey) and dimension (not required)
   * If byKey is set it will translate the value from provided key,
   * otherwise it will search for th traanslation value and return the key
   *
   * @param {sting} value
   * @param {sting} dimension
   * @param {sting} byKey
   */
  translateFilterParameter(
    value: string,
    dimension?: string,
    byKey = true,
    possibleDimensions?: Array<string>,
    isInPath = false,
  ): string | Array<string> | null {
    const translations = this.filterUrlTranslations[dimension];
    const inPathTranslations = this.filterUrlTranslations.inPath && this.filterUrlTranslations.inPath[dimension];

    if (byKey && isInPath && inPathTranslations) {
      const inPathTranslation = inPathTranslations[value];

      if (inPathTranslation) {
        return inPathTranslation;
      }
    }

    if (byKey && translations) {
      return translations[value] || value;
    }

    if (!dimension && !byKey) {
      const translation = Object.keys(this.filterUrlTranslations).reduce((accumulator, dimensionKey) => {
        let accumulatedValue = [...accumulator];

        if (possibleDimensions && possibleDimensions.indexOf(dimensionKey) === -1) {
          return accumulatedValue;
        }

        const inPathDimensionValues =
          this.filterUrlTranslations.inPath && this.filterUrlTranslations.inPath[dimensionKey];

        if (inPathDimensionValues) {
          const inPathTranslatedValue = Object.keys(inPathDimensionValues).reduce((accumulatorValue, filterKey) => {
            if (inPathDimensionValues[filterKey] === value) {
              return [...accumulatorValue, filterKey];
            }

            return accumulatorValue;
          }, []);

          if (inPathTranslatedValue) {
            accumulatedValue = [...accumulatedValue, ...inPathTranslatedValue];
          }
        }

        const dimensionValues = this.filterUrlTranslations[dimensionKey];
        const dimensionsConfig = this.getConfig();

        Object.keys(dimensionValues).forEach((translatedKey) => {
          const dimensionConfig = mapParameterToConfig(dimensionKey, dimensionsConfig.dimensions);

          if (dimensionValues[translatedKey] === value && dimensionConfig && dimensionConfig.inPath === true) {
            accumulatedValue = [...accumulatedValue, translatedKey];
          }
        });
        return accumulatedValue;
      }, []);

      if (translation && translation.length) {
        return translation.length === 1 ? translation[0] : translation;
      }

      return null;
    }

    if (dimension && translations && !byKey) {
      // let translation = null;
      const foundTranslations = Object.keys(translations).reduce(
        (accumulator, translatedKey) =>
          translations[translatedKey] === value ? [...accumulator, translatedKey] : accumulator,
        [],
      );

      if (foundTranslations.length) {
        return foundTranslations.length === 1 ? foundTranslations[0] : foundTranslations;
      }
    }

    return value;
  }

  /**
   * translateFilterValue
   *
   * An auxiliary function to check if the values are of type string or array
   * and use translateFilterParameter to translate. The return value type will be same as input type
   * If byKey is set it will translate the value from provided key,
   * otherwise it will search for th traanslation value and return the key
   *
   * @param {string | Array<string>} filter
   * @param {string} dimension?
   * @param {boolean} byKey?
   */
  translateFilterValue(
    filter: string | Array<string>,
    dimension?: string,
    byKey = true,
    possibleDimensions?: Array<string>,
    isInPath = false,
  ): string | Array<string> | null {
    return Array.isArray(filter)
      ? filter.reduce((accumulator, value) => {
          const translations = this.translateFilterParameter(value, dimension, byKey, possibleDimensions, isInPath);

          // $FlowFixMe
          return [...accumulator, ...(Array.isArray(translations) ? translations : [translations])];
        }, [])
      : this.translateFilterParameter(filter, dimension, byKey, possibleDimensions, isInPath);
  }

  /**
   * translateQueryString
   *
   * @param {string | Record<string, any>} queryString
   * @returns {Record<string, any>}
   */
  translateQueryString(queryString: string | Record<string, any>): Record<string, any> {
    const parsedQueryString: Record<string, any> =
      typeof queryString === 'string' ? parseQueryString(queryString) : queryString;

    return Object.keys(parsedQueryString).reduce((accumulator, qsParam) => {
      const qsValue = parsedQueryString[qsParam];

      const dimension = this.translateQueryStringParameter(qsParam, false);

      if (!dimension) {
        return accumulator;
      }

      const value = this.translateFilterValue(qsValue, dimension, false);

      accumulator[dimension] = value;

      return accumulator;
    }, {});
  }

  /**
   * getParentDimension
   *
   * @param {string} filterKey
   * @returns {null | string}
   */
  getParentDimension(filterKey: string): null | string {
    let parentDimension = null;
    const filterConfig = this.getConfig();
    const hasParentDimension =
      (!filterConfig.dimensions[filterKey] &&
        Object.keys(filterConfig.dimensions).some((dimensionKey) => {
          const dimensionConfig = filterConfig.dimensions[dimensionKey];
          if (dimensionConfig && dimensionConfig.childDimension && dimensionConfig.childDimension[filterKey]) {
            parentDimension = dimensionKey;
            return true;
          }
          return false;
        })) ||
      null;

    return (hasParentDimension && parentDimension) || null;
  }

  /**
   * isNoindexFilter
   *
   * @param {Record<string, any>} filters
   * @returns {boolean};
   */
  isNoindexFilter(filters: Record<string, any> = this.filters, assembledFilterUrl: string = null): boolean {
    const { dimensions } = this.getConfig();
    const filterKeys = Object.keys(filters);
    let isUrlInIndexedList = null;

    // Check if assembled url is in the list of indexed urls.
    // If not then it is noindex
    if (assembledFilterUrl && Array.isArray(this.filterConfig.indexedUrls) && this.filterConfig.indexedUrls.length) {
      const stripRootPathPattern = new RegExp(`^${this.filterConfig.rootUrlPath}/?`);

      isUrlInIndexedList =
        this.filterConfig.indexedUrls.indexOf(assembledFilterUrl.replace(stripRootPathPattern, '')) > -1;
    }

    return (
      filterKeys.indexOf('q') > -1 ||
      filterKeys.indexOf('v') > -1 ||
      filterKeys.indexOf('l') > -1 ||
      filterKeys.some(
        (key) => !checkDimensionValueConfigMeta(filters[key], mapParameterToConfig(key, dimensions).noindex),
      ) ||
      ([false, true].indexOf(isUrlInIndexedList) > -1 && !isUrlInIndexedList)
    );
  }

  /**
   * combineNonDefaultValues
   *
   * @param {Record<string, any>} filter
   * @param {Record<string, any>} parameters
   * @param {Record<string, any>} excludedValues
   * @returns {Record<string, any>}
   */
  combineNonDefaultValues(
    filter: Record<string, any>,
    parameters: Record<string, any>,
    excludedValues = this.defaultParamValues,
  ): Record<string, any> {
    return pickBy({ ...filter, ...parameters }, (item: any, key: string) => !isEqual(item, excludedValues[key]));
  }

  /**
   * add
   *
   * @param {Record<string, any>} filter
   * @param {values} values
   * @returns {Record<string, any>}
   */
  add(filter: Record<string, any>, values: Record<string, any>): Record<string, any> {
    return this.update(filter, values)(updateValues);
  }

  /**
   * remove
   *
   * @param {Record<string, any>} filter
   * @param {Record<string, any>} values
   * @param {Array<string>} dimensions
   * @returns {Record<string, any>}
   */
  remove(filter: Record<string, any>, values: Record<string, any>, dimensions?: Array<string>): Record<string, any> {
    return this.update(filter, values, dimensions)(removeValues);
  }

  /**
   * update
   *
   * This is a helper function which wraps similar functionality for updating (adding|removing)
   * values and items in the filter. It first passes specific parameters for each updateHandler and then
   * just passes the updateHandle function with the behaviour that will change the filter
   *
   * @param {Record<string, any>} filter
   * @param {Record<string, any>} values
   * @param {Array<string>} dimensions
   * @return {Function}
   */
  update(
    filter: Record<string, any>,
    values: Record<string, any>,
    dimensions?: Array<string>,
  ): (updateHandler: any) => Record<string, any> {
    const dimensionsConfig = this.getConfig().dimensions;
    const hasFiltersKey = has(filter, FILTERS_KEY);
    const flattenKeys = flattenByKey(filter, FILTERS_KEY);

    return (updateHandler: any): Record<string, any> => {
      const updatedFilters = updateHandler(flattenKeys, values, dimensions);
      const newFilter = structUrlParams(validatedUrlParams(updatedFilters, dimensionsConfig), dimensionsConfig);

      return hasFiltersKey ? newFilter : newFilter.filters;
    };
  }

  /**
   * flatToHierarchicalFilter
   *
   * @param {Record<string, any>} filter
   * @returns {Record<string, any>}
   */
  flatToHierarchicalFilter(filter: Record<string, any>): Record<string, any> {
    const dimensionsConfig = this.getConfig().dimensions;

    return reduce(
      filter,
      (res: Record<string, any>, item: Array<string>, key: string) => {
        const configItem = dimensionsConfig[key];

        if (!configItem) {
          return res;
        }

        let childKey;
        let filterChildDimension = [];

        if (configItem.childDimension && !isEmpty(configItem.childDimension)) {
          const [childKeyFromParent] = Object.keys(configItem.childDimension);
          childKey = childKeyFromParent;
          filterChildDimension = filter[childKey];
        }

        const newItem = isArray(item) ? item : [item];

        res[key] = zipObject(
          newItem,
          newItem.map((i) => {
            const childValues = configItem.values[i];
            if (!childValues) {
              return {};
            }

            const selectedChildValues = intersection(filterChildDimension, Object.keys(childValues));
            if (isEmpty(selectedChildValues)) {
              return {};
            }

            return {
              [childKey]: zipObject(
                selectedChildValues,
                selectedChildValues.map(() => ({})),
              ),
            };
          }),
        );

        return res;
      },
      {},
    );
  }

  /**
   * Creates filter options from current filter and available filter dimensions
   *
   * @param {Record<string, any>} currentFilter
   * @param {Array<Record<string, any>>} availableDimensions
   * @param {Record<string, any>} parameters
   * @returns {*}
   */
  optionsFactoryDimensions(
    currentFilter: Record<string, any> = {},
    availableDimensions: Array<Record<string, any>>,
    parameters: Record<string, any>,
  ): Array<Record<string, any>> {
    // Filter out any available values which are not present in the dimension values list in frontend application
    const validAvailableDimensions = availableDimensions.map((item) => {
      const config = this.getConfig();
      const { dimensionName, values } = item;
      const dimensionConfig = config.dimensions[dimensionName];
      const filterConfigDimensionValues = get(config.dimensions[dimensionName], 'values', {});

      return {
        ...item,
        values:
          (values &&
            values.filter(
              (value) =>
                (dimensionConfig.type === 'range' && dimensionConfig.rangeType === 'continuous') ||
                filterConfigDimensionValues[value.value],
            )) ||
          {},
      };
    });

    const options = {
      ...this.optionsFactoryListDimensions(currentFilter, validAvailableDimensions, parameters),
      ...this.optionsFactoryRangeDimensions(currentFilter, validAvailableDimensions),
    };
    return options;
  }

  /**
   * Creates filter options for range type dimensions from current filter and available filter dimensions
   *
   * @param {Object} currentFilter
   * @param {Array} availableDimensions
   * @returns {Array}
   */
  optionsFactoryRangeDimensions(
    currentFilter: Record<string, any> = {},
    availableDimensions: Array<Record<string, any>>,
  ): Array<Record<string, any>> {
    const dimensionConfig = this.getConfig().dimensions;
    const options = [];
    if (!availableDimensions) {
      return options;
    }

    // eslint-disable-next-line no-param-reassign
    currentFilter = currentFilter || {};
    availableDimensions.forEach((dimension) => {
      const { dimensionName } = dimension;
      if (dimensionConfig[dimensionName] && dimensionConfig[dimensionName].type === 'range') {
        options[dimensionName] = [];

        const dimensionValues = Object.keys(dimensionConfig[dimensionName].values);

        // Sort items by values
        if (dimensionConfig[dimensionName].sortBy === 'values') {
          dimension.values.sort((a, b) => dimensionValues.indexOf(a.value) - dimensionValues.indexOf(b.value));
        }

        if (dimensionConfig[dimensionName].rangeType === 'continuous') {
          // Options for continuous ranges (i.e. numeric ranges)
          // Get max and min available and selected values
          const available = [
            parseInt(dimensionValues[0], 10),
            parseInt(dimensionValues[dimensionValues.length - 1], 10) + 1,
          ];
          const selected = [-1, -1];
          if (dimension.values instanceof Array && dimension.values.length > 0) {
            available[0] = parseFloat(dimension.values[0].value);
            if (dimension.values.length > 1) {
              available[1] = parseFloat(dimension.values[dimension.values.length - 1].value);
            } else {
              available[1] = available[0];
            }
          }
          if (typeof currentFilter[dimensionName] === 'string') {
            const selectedValues = currentFilter[dimensionName].split(dimensionConfig[dimensionName].rangeSeparator);
            selected[0] = this.convertDimension(parseInt(selectedValues[0], 10), dimensionName, {
              reversed: true,
            });
            if (selectedValues.length > 1 && !Number.isNaN(parseInt(selectedValues[1], 10))) {
              selected[1] = this.convertDimension(parseInt(selectedValues[1], 10), dimensionName, {
                reversed: true,
              });
            }
          }

          // Filter available values
          const availableValues = [];
          dimensionValues.forEach((dimensionValue, index) => {
            const thisValue = parseInt(dimensionValue, 10);
            const prevValue = index > 0 ? parseInt(dimensionValues[index - 1], 10) : thisValue;
            const nextValue = index < dimensionValues.length - 1 ? parseInt(dimensionValues[index + 1], 10) : thisValue;
            if (
              (thisValue >= available[0] && thisValue <= available[1]) ||
              (thisValue < available[0] && nextValue > available[0]) ||
              (thisValue > available[1] && prevValue < available[1])
            ) {
              availableValues.push(thisValue);
            }
          });

          // Add available options
          const initValue = availableValues.length > 0 ? availableValues[0] : -1;
          const finalValue = availableValues.length > 0 ? availableValues[availableValues.length - 1] : -1;
          const lastValue = parseInt(dimensionValues[dimensionValues.length - 1], 10);
          availableValues.forEach((availableValue, index) => {
            const thisValue = availableValue;
            const prevValue = index > 0 ? availableValues[index - 1] : thisValue;
            const nextValue = index < availableValues.length - 1 ? availableValues[index + 1] : thisValue;
            const isSelected =
              (selected[0] < initValue && thisValue === initValue) ||
              (selected[0] >= thisValue && selected[0] < nextValue) ||
              (dimensionConfig[dimensionName].rangeUpperBound &&
                selected[0] === thisValue &&
                thisValue === lastValue) ||
              (selected[1] < 0 &&
                ((thisValue >= available[1] && prevValue < available[1]) ||
                  (!dimensionConfig[dimensionName].rangeUpperBound && index === dimensionValues.length - 1))) ||
              (selected[1] > prevValue && (selected[1] <= thisValue || thisValue === finalValue));
            options[dimensionName].push(
              this.factoryOptionTemplate(
                this.getTranslatedKey(`${dimensionName}.${thisValue}.label`, dimensionName, thisValue),
                thisValue,
                null,
                isSelected,
                this.getTranslatedKey(`${dimensionName}.${thisValue}.helper`, dimensionName, thisValue),
                false,
                dimensionConfig[dimensionName],
              ),
            );
          });

          // Add upper bound option
          if (available[1] > lastValue) {
            if (typeof dimensionConfig[dimensionName].rangeUpperBound !== 'undefined') {
              const isSelected = selected[1] < 0 || selected[1] > lastValue;
              options[dimensionName].push(
                this.factoryOptionTemplate(
                  this.getTranslatedKey(
                    `${dimensionName}.${dimensionConfig[dimensionName].rangeUpperBound}.label`,
                    dimensionName,
                  ),
                  dimensionConfig[dimensionName].rangeUpperBound,
                  null,
                  isSelected,
                  this.getTranslatedKey(
                    `${dimensionName}.${dimensionConfig[dimensionName].rangeUpperBound}.helper`,
                    dimensionName,
                  ),
                  false,
                  dimensionConfig[dimensionName],
                ),
              );
            }
          }
        } else if (dimensionConfig[dimensionName].rangeType === 'discreet') {
          // Options for discreet ranges
          // Get max and min selected values
          const selected = [-1, -1];
          if (typeof currentFilter[dimensionName] === 'string') {
            const selectedValues = currentFilter[dimensionName].split(dimensionConfig[dimensionName].rangeSeparator);
            selected[0] = dimensionValues.indexOf(selectedValues[0]);
            if (selectedValues.length > 1) {
              selected[1] = dimensionValues.indexOf(selectedValues[1]);
            }
          }

          if (dimension.values instanceof Array) {
            // Process each dimension value
            dimension.values.forEach((value, index) => {
              const valueKey = dimensionValues.indexOf(value.value);
              const isSelected =
                valueKey === selected[0] ||
                valueKey === selected[1] ||
                (selected[0] < valueKey && index === 0) ||
                ((selected[1] < 0 || selected[1] > valueKey) && index === dimension.values.length - 1);
              options[dimensionName].push(
                this.factoryOptionTemplate(
                  value.display !== null
                    ? value.display
                    : this.getTranslatedKey(`${dimensionName}.${value.value}.label`, dimensionName),
                  value.value,
                  null,
                  isSelected,
                  this.getTranslatedKey(`${dimensionName}.${value.value}.helper`, dimensionName),
                  false,
                  dimensionConfig[dimensionName],
                ),
              );
            });
          }
        }
      }
    });

    return options;
  }

  /**
   * Creates filter options for list type dimensions from current filter and available filter dimensions
   *
   * @param {Record<string, any>} currentFilter
   * @param {Array<Record<string, any>>} availableDimensions
   * @param {Record<string, any>} parameters
   * @returns {Record<string, any>}
   */
  optionsFactoryListDimensions(
    currentFilter: Record<string, any>,
    availableDimensions: Array<Record<string, any>>,
    parameters: Record<string, any>,
  ): Record<string, any> {
    const options = this.optionsFactorySelectedListDimensions(currentFilter, parameters);
    if (availableDimensions) {
      this.optionsFactoryAvailableListDimensions(currentFilter, parameters, availableDimensions, options);
    }

    return options;
  }

  /**
   * Creates filter options for list type dimensions from current filters
   *
   * @param {Record<string, any>} currentFilter
   * @param {Record<string, any>} parameters
   * @returns {Record<string, any>}
   */
  optionsFactorySelectedListDimensions(
    currentFilter: Record<string, any>,
    parameters: Record<string, any>,
  ): Record<string, any> {
    // Cheeseburger free to anyone who's able to understand this
    const dimensionConfig = this.getConfig().dimensions;
    const hierarchicalFilter = this.flatToHierarchicalFilter(currentFilter);
    const options = {};

    Object.keys(hierarchicalFilter).forEach((dimension) => {
      if (dimensionConfig[dimension].type === 'list') {
        options[dimension] = this.recursiveLoopOverSelectedListDimensions(
          currentFilter,
          parameters,
          dimension,
          hierarchicalFilter[dimension],
        );

        // Add remove dimension option
        if (dimensionConfig[dimension].allowClearAll !== false) {
          if (options[dimension].length > 0) {
            const assembledFilterUrl = this.assemble(this.remove(currentFilter, {}, [dimension]), parameters);
            options[dimension].push(
              this.factoryOptionTemplate(
                this.getTranslator()({ id: 'components.filters.clearAll' }),
                null,
                assembledFilterUrl,
                false,
                null,
                this.isNoindexFilter(
                  this.combineNonDefaultValues(this.remove(currentFilter, {}, [dimension]), parameters),
                  assembledFilterUrl,
                ),
                dimensionConfig[dimension],
              ),
            );
          }
        }
      }
    });

    return options;
  }

  /**
   * Recursively parse list type dimensions hierarchy from current filter and create options array
   *
   * @param {Object} currentFilter
   * @param {Object} parameters
   * @param {string} dimension
   * @param {Object} hierarchicalFilter
   * @param {Object} prefix
   * @returns {Array}
   */
  recursiveLoopOverSelectedListDimensions(
    currentFilter: Record<string, any>,
    parameters: Record<string, any>,
    dimension: string,
    hierarchicalFilter: Record<string, any>,
    prefix: Record<string, any> = {},
  ): Array<Record<string, any>> {
    let options = [];
    Object.keys(hierarchicalFilter).forEach((value) => {
      // Copy prefix object and assign this level values
      const prefixCopy =
        typeof prefix.filter === 'undefined' ? { label: null, value: null, filter: {} } : cloneDeep(prefix);
      prefixCopy.label = prefixCopy.label
        ? prefixCopy.label + OPTION_LABEL_SEPARATOR + this.getTranslatedKey(`${dimension}.${value}`, dimension, value)
        : this.getTranslatedKey(`${dimension}.${value}`, dimension, value);
      prefixCopy.value = prefixCopy.value ? prefixCopy.value + OPTION_VALUE_SEPARATOR + value : value;
      prefixCopy.filter[dimension] = [value];

      if (Object.keys(hierarchicalFilter[value]).length > 0) {
        // Process children dimensions
        Object.keys(hierarchicalFilter[value]).forEach((subDimension) => {
          options = concat(
            options,
            this.recursiveLoopOverSelectedListDimensions(
              currentFilter,
              parameters,
              subDimension,
              hierarchicalFilter[value][subDimension],
              prefixCopy,
            ),
          );
        });
      } else {
        const newFilter = this.remove(
          currentFilter,
          Object.keys(hierarchicalFilter).length > 1 ? { [dimension]: [value] } : prefixCopy.filter,
        );
        const assembledFilterUrl = this.assemble(newFilter, parameters);
        options.push(
          this.factoryOptionTemplate(
            prefixCopy.label,
            prefixCopy.value,
            assembledFilterUrl,
            true,
            null,
            this.isNoindexFilter(this.combineNonDefaultValues(newFilter, parameters), assembledFilterUrl),
            this.getConfig().dimensions[dimension],
          ),
        );
      }
    });

    return options;
  }

  /**
   *
   *
   * @param {Record<string, any>} currentFilter
   * @param {Record<string, any>} parameters
   * @param {Array<Record<string, any>>} availableDimensions
   * @param {Record<string, any>} currentOptions
   * @return void
   */
  optionsFactoryAvailableListDimensions(
    currentFilter: Record<string, any>,
    parameters: Record<string, any>,
    availableDimensions: Array<Record<string, any>>,
    currentOptions: Record<string, any> = {},
  ): void {
    const dimensionConfig = this.getConfig().dimensions;
    if (availableDimensions) {
      availableDimensions.forEach((dimension) => {
        const { dimensionName } = dimension;
        if (dimensionConfig[dimensionName] && dimensionConfig[dimensionName].type === 'list') {
          // Create the section if is not in the options
          if (typeof currentOptions[dimensionName] === 'undefined') {
            // eslint-disable-next-line no-param-reassign
            currentOptions[dimensionName] = [];
          }

          // eslint-disable-next-line no-param-reassign
          currentOptions[dimensionName] = concat(
            currentOptions[dimensionName],
            this.recursiveLoopOverAvailableListDimensions(
              currentFilter,
              currentOptions[dimensionName],
              parameters,
              dimension,
            ),
          );

          // Sort dimensions
          if (dimensionConfig[dimensionName].sortBy === 'values') {
            const dimensionValues = Object.keys(dimensionConfig[dimensionName].values);
            currentOptions[dimensionName].sort(
              (a, b) => dimensionValues.indexOf(a.value) - dimensionValues.indexOf(b.value),
            );
          }
        }
      });
    }
  }

  /**
   *
   * @param {Record<string, any>} currentFilter
   * @param {Array<Record<string, any>>} currentOptions
   * @param {Record<string, any>} parameters
   * @param {Record<string, any>} dimension
   * @param {Record<string, any>} prefix
   * @returns {Array<Record<string, any>>}
   */
  recursiveLoopOverAvailableListDimensions(
    currentFilter: Record<string, any>,
    currentOptions: Array<Record<string, any>>,
    parameters: Record<string, any>,
    dimension: Record<string, any>,
    prefix: Record<string, any> = {},
  ): Array<Record<string, any>> {
    let options = [];
    if (dimension.values instanceof Array) {
      // Process each dimension value
      dimension.values.forEach((value: Record<string, any>) => {
        // Copy prefix object and assign this level values
        const displayValue =
          value.display !== null
            ? value.display
            : this.getTranslatedKey(`${dimension.dimensionName}.${value.value}`, dimension.dimensionName, value.value);

        const prefixCopy =
          typeof prefix.filter === 'undefined' ? { label: null, value: null, filter: {} } : cloneDeep(prefix);
        prefixCopy.label = prefixCopy.label ? prefixCopy.label + OPTION_LABEL_SEPARATOR + displayValue : displayValue;
        prefixCopy.value = prefixCopy.value ? prefixCopy.value + OPTION_VALUE_SEPARATOR + value.value : value.value;
        prefixCopy.filter[dimension.dimensionName] = [value.value];

        if (value.value && !currentOptions.some((item) => item.value === prefixCopy.value)) {
          const newFilter = this.add(
            this.remove(currentFilter, {
              [dimension.dimensionName]: [value.value],
            }),
            prefixCopy.filter,
          );
          const assembledFilterUrl = this.assemble(newFilter, parameters);

          options.push(
            this.factoryOptionTemplate(
              prefixCopy.label,
              prefixCopy.value,
              assembledFilterUrl,
              false,
              null,
              this.isNoindexFilter(this.combineNonDefaultValues(newFilter, parameters), assembledFilterUrl),
              dimension,
            ),
          );
        }

        // Process child dimension
        if (value.child) {
          options = concat(
            options,
            this.recursiveLoopOverAvailableListDimensions(
              currentFilter,
              currentOptions,
              parameters,
              value.child,
              prefixCopy,
            ),
          );
        }
      });
    }

    return options;
  }

  /**
   * factoryOptionTemplate
   *
   * @param {label} label
   * @param {string} value
   * @param {string} url
   * @param {boolean} selected
   * @param {string} helper
   * @param {boolean} isNoFollow
   * @param {Record<string, any>} config
   * @returns {{label: string, value: string, url: string, selected: boolean}}
   */
  factoryOptionTemplate(
    label: string | null,
    value: string | null,
    url: string | null,
    selected: boolean,
    helper: string | null = null,
    isNoFollow = false,
    config?: Record<string, any>,
  ): Record<string, any> {
    return {
      label,
      value,
      helper,
      url,
      selected,
      isNoFollow,
      config,
      translationDomain: this.getConfig().translationDomain,
    };
  }

  /**
   * factoryRangeValue
   *
   * @param {string} dimension
   * @param {Array<string>} values
   * @returns {String}
   */
  factoryRangeValue(dimension: string, values: Array<number>, availableValues: Array<number> = []): string {
    const newValues = [...values];
    const config = this.getConfig();
    const dimensionConfig = config && config.dimensions && config.dimensions[dimension];
    const separator = (dimensionConfig && dimensionConfig.rangeSeparator) || '-';

    if (dimensionConfig.rangeType === 'continuous' && newValues.length === 2 && newValues[0] === newValues[1]) {
      const configValues: number[] = Object.keys(dimensionConfig.values)
        .map((value) => parseInt(value, 10))
        .filter((value) => value <= availableValues[1]);
      const totalValues = configValues.length;
      const [min, max] = newValues;
      const maxIsUpperBound = max === dimensionConfig.rangeUpperBound;

      if (maxIsUpperBound || max >= configValues[totalValues - 1]) {
        newValues[0] = configValues[maxIsUpperBound ? totalValues - 1 : totalValues - 2];
      } else {
        newValues[1] = configValues.find((val) => val > min);
      }
    }

    return newValues.join(separator);
  }

  /**
   * calcTotalActiveFilters
   *
   * @param {Record<string, any>} options
   * @returns {number} active filters count
   */
  calcTotalActiveFilters(options: Record<string, any>): number {
    return reduce(
      options,
      (result: number, item: Array<Record<string, any>>, key: string) => {
        const itemLength = item.length;
        // check range values
        if (
          mapParameterToConfig(key, this.getConfig().dimensions).type === 'range' &&
          itemLength > 1 &&
          item[0].selected &&
          item[itemLength - 1].selected
        ) {
          return (item.slice(1, -1) || []).some((i) => i.selected) ? result + 1 : result;
        }

        return item.some((i) => i.selected) ? result + 1 : result;
      },
      0,
    );
  }

  /**
   * getMappedFilter
   *
   * @param {string} from
   * @param {string} to
   * @param {string} dimension
   * @param {string} value
   * @returns {string}
   */
  static getMappedFilter(from: string, to: string, dimension: string, value: string): string | null {
    let isReversed = false;
    const mappers = DIMENSIONS.mappers[dimension];
    const foundMapper =
      mappers &&
      mappers.find((mapper) => {
        if (mapper.from === from && mapper.to === to) {
          return true;
        }

        // Check if reversed mapped values exists and map the values based on the reversed values
        if (mapper.from === to && mapper.to === from) {
          isReversed = true;
          return true;
        }

        return false;
      });

    if (foundMapper && foundMapper.values) {
      if (isReversed) {
        return Object.keys(foundMapper.values).reduce((accumulator, key) => {
          if (!accumulator && foundMapper.values[key] === value) {
            return key;
          }

          return accumulator;
        }, undefined);
      }
      return foundMapper.values[value];
    }
  }

  /**
   * mapFilters
   *
   * @param {Record<string, any>} filters
   * @returns {Record<string, any>}
   */
  static mapFilters(filters: Record<string, any>, from: string, to: string): Record<string, any> {
    return Object.keys(filters).reduce((accumulator, dimensionKey) => {
      const foundValue = FilterService.getMappedFilter(from, to, dimensionKey, filters[dimensionKey]);

      if (foundValue) {
        return { ...accumulator, [dimensionKey]: foundValue };
      }

      return accumulator;
    }, {});
  }

  /**
   * validate
   *
   * @param {Record<string, any>} filters
   * @returns {Record<string, any>}
   */
  validate(filters: Record<string, any>): Record<string, any> {
    return validatedUrlParams(filters, this.getConfig().dimensions);
  }

  /**
   * getChildDimensions
   *
   * @param {string} parentDimension
   * @param {string} parentValue
   * @returns {Record<string, any>}
   */
  getChildDimensions(parentDimension: string, parentValue: string): Record<string, any> {
    const parentConfig = this.getConfig().dimensions[parentDimension];

    return parentConfig.values[parentValue];
  }

  /**
   * getSimilarFilters
   *
   * @param {Record<string, any>} inputFilters
   * @returns {Record<string, any>[]}
   */
  getSimilarFilters(inputFilters: Record<string, any>): Record<string, any>[] {
    const validatedFilters = this.validate(inputFilters);
    const { medium: mediums } = validatedFilters;
    const { frontendDomain } = this.getConfig();
    const styles = this.getConfig().dimensions.style.values;
    let results = [];

    if (mediums) {
      // Get all mediums with their submediums if any
      mediums.forEach((medium) => {
        const subMediums = this.getChildDimensions('medium', medium);

        if (subMediums) {
          results = [
            ...results,
            ...Object.keys(subMediums).map((subMedium) => {
              const url = this.assemble({ medium, subMedium });
              const title = this.translator(
                { id: 'components.filters.parentChildDimensions' },
                {
                  dimension1: this.translator({
                    id: `filters.${frontendDomain}.medium.${medium}`,
                  }),
                  dimension2: this.translator({
                    id: `filters.${frontendDomain}.subMedium.${subMedium}`,
                  }),
                },
              );

              return { url, title, filters: { medium, subMedium } };
            }),
          ];
        }

        // Combine mediums with styles
        results = [
          ...results,
          ...Object.keys(styles).map((style) => {
            const url = this.assemble({ medium, style });
            const title = this.translator(
              { id: 'components.filters.mediumStyleLabel' },
              {
                medium: this.translator({
                  id: `filters.${frontendDomain}.medium.${medium}`,
                }),
                style: this.translator({
                  id: `filters.${frontendDomain}.style.${style}`,
                }),
              },
            );

            return { url, title, filters: { medium, style } };
          }),
        ];
      });
    } else if (styles) {
      // If no medium is provided, render links for styles only
      results = [
        ...results,
        ...Object.keys(styles).map((style) => {
          const url = this.assemble({ style });
          const title = this.translator({
            id: `filters.${frontendDomain}.style.${style}`,
          });

          return { url, title, filters: { style } };
        }),
      ];
    }

    return results;
  }

  /**
   * parseFromDb
   *
   * @param {string} stringifiedFilter
   * @param {(domain: string, dimension: string, value: string) => string} transformation
   * @returns {Record<string, any>} parsed (and transformed) filters from database
   */
  parseFromDb(
    stringifiedFilter: string,
    transformation?: (domain: string, dimension: string, value: string) => string,
  ): Record<string, any> {
    const blocks = stringifiedFilter.split(/\s*(AND)\s*/g);
    const filters: Record<string, any> = {};

    blocks.forEach((block: string) => {
      const [matched, filterDimension, value] = block.match(/filter.(\w+)\s*=\s*(?:'([^']*)'|(\S+))/) || [];

      if (matched && filterDimension && value) {
        const dimension = Utils.snakeToCamelCase(filterDimension.trim());
        filters[dimension] = [
          ...(filters[dimension] || []),
          typeof transformation === 'function'
            ? transformation(this.filterConfig.domain, dimension, value)
            : value.trim(),
        ];
      }
    });

    return filters;
  }

  /**
   * Check if conditional meta (canonical or noindex) should be applied
   * depending on dimension configuration and actual filter values
   *
   * @param {string[]} filterValue
   * @param {'all' | 'none' | 'single' | 'multiple'} condition
   */
  static isConditionalMetaApplied(filterValue: string[], condition: 'all' | 'none' | 'single' | 'multiple'): boolean {
    const isFilterArray = Array.isArray(filterValue);
    const totalFilterValues = (isFilterArray && filterValue.length) || 0;
    const hasSetValue =
      (isFilterArray && totalFilterValues > 0) ||
      (!isFilterArray && (filterValue !== undefined || filterValue !== null));

    // No condition set or condition is 'none'
    if (!condition || (condition === 'none' && hasSetValue)) {
      return true;
    }

    // Single value
    if (condition === 'single' && ((!isFilterArray && hasSetValue) || (isFilterArray && totalFilterValues === 1))) {
      return false;
    }

    // Multiple values
    if (condition === 'multiple' && isFilterArray && totalFilterValues > 1) {
      return false;
    }

    // All values
    if (condition === 'all' && ((isFilterArray && totalFilterValues > 0) || (!isFilterArray && hasSetValue))) {
      return false;
    }

    return true;
  }
}
